import React, { useRef, useState } from 'react';

// Define TooltipProps interface for chart tooltips
interface TooltipProps<TValue, TName> {
  active?: boolean;
  payload?: Array<{
    name?: TName;
    value?: TValue;
    color?: string;
  }>;
  label?: string;
}
// TODO: Replace with ECharts radar chart implementation
// import {
//   RadarChart as RechartsRadarChart,
//   PolarGrid,
//   PolarAngleAxis,
//   PolarRadiusAxis,
//   Radar,
//   Legend,
//   ResponsiveContainer,
//   Tooltip,
//   TooltipProps
// } from 'recharts';
import { 
  styled, 
  Box, 
  Typography, 
  useTheme, 
  IconButton,  
  Tooltip as MuiTooltip,
  Menu,
  MenuItem,
  CircularProgress 
} from '@mui/material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { exportChartToPng, exportChartDataToCsv, handleChartExportToPdf } from '../../utils/chartUtils';
import { convertRadarChartData } from '../../utils/chartDataConverters';

export interface RadarChartDataItem {
  attribute: string;
  [key: string]: string | number;
}

export interface RadarSeries {
  dataKey: string;
  name?: string;
  color?: string;
  fillOpacity?: number;
}

// Add support for chart.js-like data format
export interface ChartJsRadarData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
  }[];
}

export interface RadarChartProps {
  /** Chart data - can be either RadarChartDataItem[] or ChartJsRadarData */
  data: RadarChartDataItem[] | ChartJsRadarData;
  /** Data series to display */
  series?: RadarSeries[];
  /** Chart title */
  title?: string;
  /** Chart height */
  height?: number;
  /** Format tooltips */
  tooltipFormatter?: (value: number | string) => string;
  /** Enable chart interactions (zoom, pan, export) */
  interactive?: boolean;
  /** Data is refreshing status */
  isRefreshing?: boolean;
  /** Function to refresh data */
  onRefresh?: () => void;
}

const ChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  position: 'relative',
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
}));

const RefreshIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(2),
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 255, 255, 0.7)',
  borderRadius: theme.shape.borderRadius,
  zIndex: 5,
}));

const CustomTooltip: React.FC<TooltipProps<number | string, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: 1,
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          {label}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography
            key={`tooltip-${index}`}
            variant="body2"
            sx={{ color: entry.color }}
          >
            {`${entry.name}: ${entry.value}`}
          </Typography>
        ))}
      </Box>
    );
  }

  return null;
};

export const RadarChart: React.FC<RadarChartProps> = ({
  data: inputData,
  series: inputSeries,
  title,
  height = 400,
  tooltipFormatter,
  interactive = true,
  isRefreshing = false,
  onRefresh,
}) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Handle both data formats (RadarChartDataItem[] and ChartJsRadarData)
  const data = React.useMemo(() => {
    // Check if data is in Chart.js format
    if (inputData && 
        !Array.isArray(inputData) && 
        'labels' in inputData && 
        'datasets' in inputData) {
      return convertRadarChartData(inputData);
    }
    return inputData as RadarChartDataItem[];
  }, [inputData]);
  
  // Process series from data if not provided
  const series = React.useMemo(() => {
    if (inputSeries) return inputSeries;
    
    // Extract series from data if not provided
    if (data && data.length > 0) {
      const firstItem = data[0];
      return Object.keys(firstItem)
        .filter(key => key !== 'attribute')
        .map(key => ({ 
          dataKey: key,
          name: key,
          color: undefined,
          fillOpacity: 0.6
        } as RadarSeries));
    }
    return [] as RadarSeries[];
  }, [inputSeries, data]);
  
  // Set initial data load timestamp
  React.useEffect(() => {
    if (Array.isArray(data) && data.length > 0 && !lastUpdated) {
      setLastUpdated(new Date());
    }
  }, [data, lastUpdated]);
  
  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };
  
  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };
  
  const handleExportPng = () => {
    handleExportMenuClose();
    exportChartToPng(chartRef, title || 'radar-chart');
  };
  
  const handleExportCsv = () => {
    handleExportMenuClose();
    exportChartDataToCsv(data, title || 'radar-chart-data');
  };
  
  const handleExportPdf = () => {
    handleExportMenuClose();
    handleChartExportToPdf(chartRef, data, title || 'radar-chart', title);
  };
  
  const handleExportPdfWithData = () => {
    handleExportMenuClose();
    handleChartExportToPdf(chartRef, data, title || 'radar-chart', title, true);
  };
  
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
      setLastUpdated(new Date());
    }
  };

  const formattedLastUpdated = lastUpdated 
    ? `Updated: ${lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}`
    : '';

  return (
    <ChartContainer>
      <ChartHeader>
        {title && <ChartTitle variant="h6">{title}</ChartTitle>}
        <ControlsContainer>
          {lastUpdated && (
            <RefreshIndicator>
              {formattedLastUpdated}
            </RefreshIndicator>
          )}
          
          {onRefresh && (
            <MuiTooltip title="Refresh data">
              <IconButton size="small" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          )}
          
          {interactive && (
            <>
              <MuiTooltip title="Export options">
                <IconButton size="small" onClick={handleExportMenuOpen}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </MuiTooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={exportMenuOpen}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={handleExportPng}>Export as PNG</MenuItem>
                <MenuItem onClick={handleExportPdf}>Export as PDF</MenuItem>
                <MenuItem onClick={handleExportPdfWithData}>Export as PDF with data table</MenuItem>
                <MenuItem onClick={handleExportCsv}>Export data as CSV</MenuItem>
              </Menu>
            </>
          )}
        </ControlsContainer>
      </ChartHeader>
      
      <ChartWrapper ref={chartRef}>
        {isRefreshing && (
          <LoadingOverlay>
            <CircularProgress size={30} />
          </LoadingOverlay>
        )}
        
        {interactive ? (
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            centerOnInit={true}
            wheel={{ step: 0.1 }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    right: 10, 
                    top: 10, 
                    zIndex: 10, 
                    display: 'flex', 
                    flexDirection: 'column',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: 1,
                    padding: 0.5
                  }}
                >
                  <MuiTooltip title="Zoom in">
                    <IconButton onClick={() => zoomIn()} size="small">
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Zoom out">
                    <IconButton onClick={() => zoomOut()} size="small">
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Reset zoom">
                    <IconButton onClick={() => resetTransform()} size="small">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </Box>
                <TransformComponent wrapperStyle={{ width: '100%', height: height }}>
                  <div style={{ width: '100%', height: '100%' }}>
                    {/* TODO: Replace with ECharts radar chart */}
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">
                          Radar Chart Visualization
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Interactive radar chart coming soon
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Chart functionality temporarily disabled during migration to ECharts
                        </Typography>
                      </Box>
                    </Box>
                  </div>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed #ccc',
              borderRadius: 2,
              backgroundColor: '#f9f9f9'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Radar Chart Visualization
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Static radar chart coming soon
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Chart functionality temporarily disabled during migration to ECharts
              </Typography>
            </Box>
          </Box>
        )}
      </ChartWrapper>
    </ChartContainer>
  );
};

export default RadarChart;
